// The guided check-in ladder.
//
// A blank box works for natural sharers and fails everyone else: guarded people,
// anyone with family politics they would rather not put in writing, and most
// people who simply freeze at "tell us about yourself". So a first check-in is
// guided, and the guiding climbs.
//
// Two levels, not three, and one prompt per level. Three tested like a quiz,
// and so did four prompts strung together.
//   Level 1 is easy and factual. Nothing here costs anything to answer.
//   Level 2 is warmer. Someone guarded can skip it and still have given the
//   crew plenty.
// Then one open lane at the end, so a natural sharer is never boxed in by the
// scaffolding built for everyone else. Three screens, all skippable.
//
// Every prompt is skippable and the screen says so out loud. Control is the
// answer to the privacy fear, and a person who knows they can skip reads further
// than one who does not.
//
// Purpose is the "same engine, one switch" from the crew setting: KEEPING UP is
// friends and family who have drifted, GETTING TO KNOW is a group that does not
// know each other yet. These are two doorways into the same depth, NOT shallow
// versus deep. Both ladders reach something real.

export type CrewPurpose = "keeping_up" | "getting_to_know";

export interface CheckInPrompt {
  id: string;
  level: 1 | 2;
  text: string;
  // Shown under the field. A concrete example does more to unfreeze someone than
  // any amount of encouragement.
  hint?: string;
}

// One prompt per level, not two.
//
// Four prompts plus the open lane was five screens to get through before a
// first check-in even reached the questions, and a first check-in is the one
// moment where length is the thing most likely to end it. So each level is a
// single prompt now, and the pair it replaces lives on in the hint, where the
// examples do the work of the extra screen without costing a screen.
const KEEPING_UP: CheckInPrompt[] = [
  {
    id: "ku-1-now",
    level: 1,
    text: "What have you been up to lately?",
    hint: "Work, school, a move, something you picked up, a trip you took. Anything that's changed since you last caught up.",
  },
  {
    id: "ku-2-people",
    level: 2,
    text: "Anything good going on with you or your people?",
    hint: "Family, your person, your kids, your friends. A win, something you're proud of, something that made you laugh. Only what you'd say out loud.",
  },
];

const GETTING_TO_KNOW: CheckInPrompt[] = [
  {
    id: "gtk-1-days",
    level: 1,
    text: "What are you into, and what do your days look like?",
    hint: "What you do for fun, what you're watching. Work, school, who you live with, where you're from.",
  },
  {
    id: "gtk-2-story",
    level: 2,
    text: "What's something about you people here probably don't know yet?",
    hint: "Where you grew up, something you used to do, something you're proud of this year. Big or small, it counts.",
  },
];

export function promptsFor(purpose: CrewPurpose): CheckInPrompt[] {
  return purpose === "getting_to_know" ? GETTING_TO_KNOW : KEEPING_UP;
}

// The open lane, for people who would rather just talk.
export const OPEN_LANE: CheckInPrompt = {
  id: "open",
  level: 2,
  text: "Anything else you want to put in?",
  hint: "Skip it if you've said your piece.",
};

export interface PromptAnswer {
  prompt: string;
  response: string;
}

// Turn what they actually filled in into the update the question writer reads.
//
// Blank answers are dropped rather than sent as empty prompts, because a skipped
// prompt is not a fact about the person and the generator should never see it as
// one. The prompt is kept alongside the answer: "my sister moved to Atlanta"
// means more when the generator can see it was said about family.
export function buildUpdateText(answers: PromptAnswer[]): string {
  return answers
    .map((a) => ({ prompt: a.prompt.trim(), response: a.response.trim() }))
    .filter((a) => a.response.length > 0)
    .map((a) => (a.prompt ? `${a.prompt}\n${a.response}` : a.response))
    .join("\n\n");
}

// Did they actually say anything? Used to route a skip-everything run to the
// open box instead of letting them submit nothing and get a failure back.
export function hasAnySubstance(answers: PromptAnswer[]): boolean {
  return answers.some((a) => a.response.trim().length > 0);
}

// How the check-in introduces itself. Same warmth either way: the difference is
// that one crew is catching up and the other is meeting.
export function checkInFraming(purpose: CrewPurpose): { first: string; returning: string; lead: string } {
  return purpose === "getting_to_know"
    ? {
        first: "Let's meet you",
        returning: "What's good?",
        lead: "A few questions so the crew gets to know you. Answer what you want, skip what you don't.",
      }
    : {
        first: "What's good?",
        returning: "What's good?",
        lead: "A few questions so the crew can catch up with you. Answer what you want, skip what you don't.",
      };
}

// One line handed to the question writer so it knows which room it is in. A
// crew that is meeting for the first time should not get questions phrased as
// though everyone already knows the person.
export function generationAngle(purpose: CrewPurpose): string {
  return purpose === "getting_to_know"
    ? "This crew is still getting to know each other, so questions should help people learn who this person is."
    : "This crew already knows each other and is catching up, so questions should be about what is new or current for this person.";
}
